import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { HubspotEvents } from '~constants/hubspot/events';
import { GlobalState } from '~reducers';
import { SessionState } from '~reducers/session';
import { HUBSPOT_PORTAL_ID, VITE_ENV_TYPES } from '~utils/config';

declare global {
	interface Window {
		_hsq?: Array<[string, unknown]>;
	}
}

const HUBSPOT_SRC = `https://js.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`;
const TRACK_CUSTOM_BEHAVIORAL_EVENT = 'trackCustomBehavioralEvent';

interface HubspotEventProperties {
	userId?: number;
	// Needed for legacy events that were tracked before we had the email in the payload. Can be removed once all legacy events are removed.
	email?: string;
	customerEmail: string;
	customerName?: string;
	businessId?: number;
	businessName?: string;
}

interface HubspotEventPayload {
	name: HubspotEvents;
	properties: HubspotEventProperties;
}

interface UseHubspotTrackerReturn {
	trackHubspotEvent: (eventName: HubspotEvents) => void;
}

export const useHubspotTracker = (): UseHubspotTrackerReturn => {
	const { userId, email, business, firstName, lastName } = useSelector(
		(state: GlobalState) => state.session as SessionState,
	);

	const isTrackingEnabled = [
		VITE_ENV_TYPES.PRE_PROD,
		VITE_ENV_TYPES.PRODUCTION,
	].includes(import.meta.env.VITE_ENV as string);

	useEffect(() => {
		if (!isTrackingEnabled) {
			return;
		}

		if (!document.getElementById('hs-script-loader')) {
			const script = document.createElement('script');
			script.id = 'hs-script-loader';
			script.async = true;
			script.defer = true;
			script.src = HUBSPOT_SRC;
			document.body.appendChild(script);
		}
	}, [isTrackingEnabled]);

	const trackHubspotEvent = useCallback(
		(eventName: HubspotEvents): void => {
			if (!isTrackingEnabled || !email || !window._hsq) {
				return;
			}

			if (email.endsWith('@trycents.com')) {
				return;
			}

			const payload: HubspotEventPayload = {
				name: eventName,
				properties: {
					userId,
					email,
					customerEmail: email,
					customerName: `${firstName ?? ''} ${lastName ?? ''}`.trim(),
					businessId: business?.id,
					businessName: business?.name,
				},
			};

			// identify call removed intentionally — prevents HubSpot from creating new contacts
			// for users who don't already exist; events still associate with existing contacts by cookie
			window._hsq.push([TRACK_CUSTOM_BEHAVIORAL_EVENT, payload]);
		},
		[isTrackingEnabled, email, userId, business, firstName, lastName],
	);

	return { trackHubspotEvent };
};
